import {
  RAIL_ICON,
  RAIL_ITEM_IDLE_BG,
  RAIL_ITEM_IDLE_TEXT,
  railItem,
  railItemFill,
  railLabel,
  sheetItem,
} from '../../components/railItem';
import { Icon } from '../../components/Icon';
import { useRailCollapsed } from '../../lib/railCollapse';
import { currentPlatform, searchShortcutLabel } from './hotkey';
import { openSearch } from './open';

const SHORTCUT = searchShortcutLabel(currentPlatform());

/**
 * The two places search can be opened by pointer.
 *
 * ── WHY THERE IS A BUTTON AT ALL, GIVEN THE SHORTCUT ──────────────────────────────────────────
 *
 * Ctrl+K is undiscoverable. Nobody has ever found a keyboard shortcut by not being shown it, and
 * the operators this product is built for arrive mid-shift on somebody else's machine. The rail
 * item is how they learn the box exists; the hint beside it is how they learn to stop using the
 * rail item.
 *
 * ── AT THE TOP OF THE RAIL, ABOVE NAVIGATION ──────────────────────────────────────────────────
 *
 * Not in the chrome cluster under the hairline, where theme and notifications live. DESIGN_SYSTEM
 * §5 says everything below that hairline is NOT a destination — and search is the fastest route to
 * every destination there is, which makes it the wrong shape for that group and the right shape
 * for the top of the list of places. It sits directly under the brand tile, where a search field
 * sits in most consoles.
 */

/** The rail's Search row. Wears the rail's one control geometry, like everything else up there. */
export function SearchRailItem() {
  const [collapsed] = useRailCollapsed();

  return (
    <button
      type="button"
      onClick={openSearch}
      // The shortcut is in the tooltip as well as beside the label, because a collapsed rail shows
      // no label at all and the tooltip is the only thing that can teach it there.
      title={collapsed ? `Search (${SHORTCUT})` : undefined}
      aria-keyshortcuts="Control+K Meta+K"
      className={`${railItem(collapsed)} ${RAIL_ITEM_IDLE_TEXT}`}
    >
      <span aria-hidden="true" className={`${railItemFill(collapsed)} ${RAIL_ITEM_IDLE_BG}`} />
      <Icon name="search" size={RAIL_ICON} className="relative shrink-0" />
      <span className={railLabel(collapsed)}>Search</span>
      {/*
       * The hint is inside the animated label, so it slides away with it when the rail collapses
       * rather than being left stranded over a 76px strip. `aria-hidden` because the shortcut is
       * already on the button as `aria-keyshortcuts`, which is the property assistive technology
       * actually reads for this.
       */}
      <kbd
        aria-hidden="true"
        className={`relative ml-auto shrink-0 rounded-sm bg-surface-sunk px-1.5 py-0.5 font-mono text-[0.625rem] text-ink-subtle transition-opacity duration-[var(--duration)] ${
          collapsed ? 'hidden opacity-0' : 'opacity-100'
        }`}
      >
        {SHORTCUT}
      </kbd>
    </button>
  );
}

/**
 * The same thing in the phone's More sheet.
 *
 * A separate control rather than the rail's reflowed, for the reason DESIGN_SYSTEM §5 gives about
 * the whole bottom bar: it wears the sheet's 44px touch geometry, and there is no shortcut hint,
 * because there is no keyboard to press it on.
 */
export function SearchSheetItem({ onNavigate }: { onNavigate: () => void }) {
  return (
    <button
      type="button"
      onClick={() => {
        // The sheet closes first. Two overlays open at once is glass on glass (§2b), and the
        // sheet would still be holding the focus trap the palette needs.
        onNavigate();
        openSearch();
      }}
      className={sheetItem()}
    >
      <Icon name="search" size={18} className="shrink-0" />
      Search
    </button>
  );
}
